import {
  commercialTermsOfUseDocument,
  commercialTermsOfUseDocumentV1_1,
  type CommercialTermsBlock,
} from './commercialTermsOfUse';
import {
  privacyPolicyDocument,
  privacyPolicyDocumentV1_1,
  type PrivacyPolicyBlock,
  type PrivacyPolicyDocument,
} from './privacyPolicy';

export type PublicMarketplaceLegalDocumentKind =
  'privacy' | 'terms' | 'cookies';

export interface PublicMarketplaceLegalDocumentSection {
  title: string;
  body: string[];
}

export interface PublicMarketplaceLegalDocumentRelease {
  kind: PublicMarketplaceLegalDocumentKind;
  version: string;
  effectiveDate: string;
  checksumSha256: string;
  counselApprovedAt?: string;
  counselApprovedBy?: string;
  canonicalPath: string;
  eyebrow: string;
  title: string;
  description: string;
  sections: PublicMarketplaceLegalDocumentSection[];
  compliance: {
    retention: string;
    legalBases: string;
    processors: string;
    transfers: string;
    rightsProcedure: string;
    responsePeriods: string;
    cookieClassification: string;
  };
}

export interface PublicMarketplaceLegalDocumentManifest {
  schemaVersion: 1;
  productionEnabled: boolean;
  releases: PublicMarketplaceLegalDocumentRelease[];
}

type CanonicalLegalBlock = CommercialTermsBlock | PrivacyPolicyBlock;

function renderCanonicalBlock(block: CanonicalLegalBlock): string[] {
  switch (block.kind) {
    case 'paragraph':
      return [block.text];
    case 'bullets':
      return [...block.items];
    case 'table':
      return block.rows.map((row) => row.join(' — '));
    case 'heading':
    case 'subheading':
      return [];
  }
}

function buildCanonicalSections(
  blocks: readonly CanonicalLegalBlock[],
  initialTitle: string
): PublicMarketplaceLegalDocumentSection[] {
  const sections: PublicMarketplaceLegalDocumentSection[] = [];
  let currentSection: PublicMarketplaceLegalDocumentSection = {
    title: initialTitle,
    body: [],
  };

  const commitCurrentSection = (): void => {
    if (currentSection.body.length > 0) {
      sections.push(currentSection);
    }
  };

  for (const block of blocks) {
    if (block.kind === 'heading' || block.kind === 'subheading') {
      commitCurrentSection();
      currentSection = { title: block.text, body: [] };
      continue;
    }
    currentSection.body.push(...renderCanonicalBlock(block));
  }
  commitCurrentSection();
  return sections;
}

function requireCanonicalParagraph(
  blocks: readonly CanonicalLegalBlock[],
  fragment: string
): string {
  const paragraph = blocks.find(
    (block): block is Extract<CanonicalLegalBlock, { kind: 'paragraph' }> =>
      block.kind === 'paragraph' && block.text.includes(fragment)
  );
  if (!paragraph) {
    throw new Error(`Approved legal source is missing: ${fragment}`);
  }
  return paragraph.text;
}

function requireCanonicalSection(
  blocks: readonly CanonicalLegalBlock[],
  heading: string
): readonly CanonicalLegalBlock[] {
  const startIndex = blocks.findIndex(
    (block) => block.kind === 'heading' && block.text === heading
  );
  if (startIndex === -1) {
    throw new Error(`Approved legal source section is missing: ${heading}`);
  }
  const remainingBlocks = blocks.slice(startIndex + 1);
  const nextHeadingOffset = remainingBlocks.findIndex(
    (block) => block.kind === 'heading'
  );
  return nextHeadingOffset === -1
    ? remainingBlocks
    : remainingBlocks.slice(0, nextHeadingOffset);
}

const cookieHeading = '12. Cookies, almacenamiento local y monitoreo';

interface PrivacyPolicyReleaseContent {
  privacyBlocks: readonly CanonicalLegalBlock[];
  cookieBlocks: readonly CanonicalLegalBlock[];
  compliance: PublicMarketplaceLegalDocumentRelease['compliance'];
}

// The cookie document and the compliance summary are excerpts of one privacy
// policy release, so every published release quotes the policy text it was
// released with.
function derivePrivacyPolicyReleaseContent(
  privacyDocument: PrivacyPolicyDocument
): PrivacyPolicyReleaseContent {
  const privacyBlocks = privacyDocument.blocks;
  const cookieBlocks = requireCanonicalSection(privacyBlocks, cookieHeading);
  return {
    privacyBlocks,
    cookieBlocks,
    compliance: {
      retention: requireCanonicalParagraph(
        privacyBlocks,
        'Los Datos Personales se conservan mientras sean necesarios'
      ),
      legalBases: requireCanonicalParagraph(
        privacyBlocks,
        'El tratamiento de datos de cuenta, acceso, seguridad, soporte y operación'
      ),
      processors: requireCanonicalParagraph(
        privacyBlocks,
        'Los Datos Personales pueden ser tratados por personal autorizado'
      ),
      transfers: requireCanonicalParagraph(
        privacyBlocks,
        'El Sistema opera sobre infraestructura cloud y proveedores tecnológicos'
      ),
      rightsProcedure: requireCanonicalParagraph(
        privacyBlocks,
        'Cuando una solicitud corresponda a datos bajo responsabilidad directa'
      ),
      responsePeriods: requireCanonicalParagraph(
        privacyBlocks,
        'El derecho de acceso se responde dentro de 20 días hábiles'
      ),
      cookieClassification: requireCanonicalParagraph(
        cookieBlocks,
        'El frontend del Sistema puede usar almacenamiento local o de sesión'
      ),
    },
  };
}

const privacyV1_1 = derivePrivacyPolicyReleaseContent(
  privacyPolicyDocumentV1_1
);
const privacyV1_2 = derivePrivacyPolicyReleaseContent(privacyPolicyDocument);

export const PUBLIC_MARKETPLACE_LEGAL_DOCUMENT_MANIFEST: PublicMarketplaceLegalDocumentManifest =
  {
    schemaVersion: 1,
    productionEnabled: true,
    releases: [
      {
        kind: 'terms',
        version: '1.1',
        effectiveDate: '2026-06-09',
        checksumSha256:
          'abcf6da5ee79956208779b829124cc6cbba82a42bda4446ee9e2f902eb5e6a8a',
        canonicalPath: '/legal/terms-website',
        eyebrow: 'Términos legales',
        title: commercialTermsOfUseDocumentV1_1.heading,
        description: commercialTermsOfUseDocumentV1_1.subtitle,
        sections: buildCanonicalSections(
          commercialTermsOfUseDocumentV1_1.blocks,
          commercialTermsOfUseDocumentV1_1.effectiveVersion
        ),
        compliance: privacyV1_1.compliance,
      },
      {
        kind: 'privacy',
        version: '1.1',
        effectiveDate: '2026-06-11',
        checksumSha256:
          '9097eb694e951cbb397dd749e4c1a2d9469f4026dcd35e447298c02ae9b4b7d8',
        canonicalPath: '/legal/privacy',
        eyebrow: 'Protección de datos personales',
        title: privacyPolicyDocumentV1_1.heading,
        description: privacyPolicyDocumentV1_1.subtitle,
        sections: buildCanonicalSections(
          privacyV1_1.privacyBlocks,
          privacyPolicyDocumentV1_1.effectiveVersion
        ),
        compliance: privacyV1_1.compliance,
      },
      {
        kind: 'cookies',
        version: '1.1',
        effectiveDate: '2026-06-11',
        checksumSha256:
          '9f6d956d30c945d5c84709dcf29847b85e1a69ecb0b1a74fb5fc0befa8df5e8b',
        canonicalPath: '/legal/cookies',
        eyebrow: 'Almacenamiento técnico',
        title: cookieHeading,
        description: privacyV1_1.compliance.cookieClassification,
        sections: buildCanonicalSections(
          privacyV1_1.cookieBlocks,
          cookieHeading
        ),
        compliance: privacyV1_1.compliance,
      },
      {
        kind: 'terms',
        version: '1.2',
        effectiveDate: '2026-10-07',
        checksumSha256:
          'fb5a5c9b5fd4b179e6f0def374e66333a0b1fe313d91e2dea572e4f3f912ef25',
        canonicalPath: '/legal/terms-website',
        eyebrow: 'Términos legales',
        title: commercialTermsOfUseDocument.heading,
        description: commercialTermsOfUseDocument.subtitle,
        sections: buildCanonicalSections(
          commercialTermsOfUseDocument.blocks,
          commercialTermsOfUseDocument.effectiveVersion
        ),
        compliance: privacyV1_2.compliance,
      },
      {
        kind: 'privacy',
        version: '1.2',
        effectiveDate: '2026-10-07',
        checksumSha256:
          '69f077b906f23e84c0b60588ed4a33936ca4ba15a958b7deb0a722dd6e5a877f',
        canonicalPath: '/legal/privacy',
        eyebrow: 'Protección de datos personales',
        title: privacyPolicyDocument.heading,
        description: privacyPolicyDocument.subtitle,
        sections: buildCanonicalSections(
          privacyV1_2.privacyBlocks,
          privacyPolicyDocument.effectiveVersion
        ),
        compliance: privacyV1_2.compliance,
      },
    ],
  };

const SHA256_PATTERN = /^[a-f0-9]{64}$/u;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;

export function serializePublicMarketplaceLegalDocument(
  release: PublicMarketplaceLegalDocumentRelease
): string {
  return JSON.stringify({
    kind: release.kind,
    version: release.version,
    effectiveDate: release.effectiveDate,
    canonicalPath: release.canonicalPath,
    eyebrow: release.eyebrow,
    title: release.title,
    description: release.description,
    sections: release.sections,
    compliance: release.compliance,
  });
}

export function resolveApprovedPublicMarketplaceLegalDocument(input: {
  manifest: PublicMarketplaceLegalDocumentManifest;
  kind: PublicMarketplaceLegalDocumentKind;
  asOfDate: string;
}): PublicMarketplaceLegalDocumentRelease | null {
  if (!input.manifest.productionEnabled) {
    return null;
  }
  if (!ISO_DATE_PATTERN.test(input.asOfDate)) {
    throw new Error('Public marketplace legal document date is invalid');
  }

  const releases = input.manifest.releases
    .filter(
      (release) =>
        release.kind === input.kind && release.effectiveDate <= input.asOfDate
    )
    .sort((left, right) =>
      right.effectiveDate.localeCompare(left.effectiveDate)
    );
  const release = releases[0];
  if (!release) {
    return null;
  }

  const requiredText = [
    release.version,
    release.canonicalPath,
    release.eyebrow,
    release.title,
    release.description,
    ...Object.values(release.compliance),
  ];
  if (
    !ISO_DATE_PATTERN.test(release.effectiveDate) ||
    !SHA256_PATTERN.test(release.checksumSha256) ||
    requiredText.some((value) => value.trim() === '') ||
    release.sections.length === 0 ||
    release.sections.some(
      (section) =>
        section.title.trim() === '' ||
        section.body.length === 0 ||
        section.body.some((paragraph) => paragraph.trim() === '')
    )
  ) {
    throw new Error(
      `Approved public marketplace ${input.kind} document is incomplete`
    );
  }

  return release;
}
